import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SurveyChatInterface from '../components/SurveyChatInterface';
import { auth } from '../config/firebase';
import { surveyAPI } from '../services/api';

const SurveyPage = () => {
  const [user, setUser] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const { accessToken } = useParams();

  // Function to start/restart session with a specific language
  const startSessionWithLanguage = async (language) => {
    if (!user || !accessToken) return;

    setLoading(true);
    setError(null);
    setSelectedLanguage(language);

    try {
      console.log('[SurveyPage] Creating session with language:', language);
      const data = await surveyAPI.startSession(accessToken, language);
      console.log('[SurveyPage] Session created:', data.sessionId);
      setSessionData(data);
    } catch (err) {
      console.error('Failed to start survey session:', err);
      setError(`Failed to start survey: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      setError("No survey access token provided.");
      setLoading(false);
      return;
    }

    // onAuthStateChanged is the source of truth for auth
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      let authUser = currentUser;

      if (!authUser) {
        // No user is signed in, so sign one in anonymously.
        try {
          // This will cause onAuthStateChanged to fire again with the new user,
          // so we just return and wait for that second trigger.
          await signInAnonymously(auth);
          return;
        } catch (err) {
          console.error("Failed to sign in anonymously", err);
          setError("Failed to initialize. Please refresh.");
          setLoading(false);
          return;
        }
      }

      // We have a user (either pre-existing or from the sign-in above)
      setUser(authUser);

      // Only start the session if we haven't already
      if (!sessionData) {
        try {
          // Force token refresh to ensure auth is stable before creating session
          // This prevents race condition where auth.currentUser might be different
          // from the user that creates the session
          console.log('[SurveyPage] Auth user UID:', authUser.uid);
          await authUser.getIdToken(true);
          console.log('[SurveyPage] Token refreshed, creating session...');

          const data = await surveyAPI.startSession(accessToken, selectedLanguage);
          console.log('[SurveyPage] Session created:', data.sessionId);
          setSessionData(data); // data = { sessionId, survey_title, survey_description, first_question }
        } catch (err) {
          console.error('Failed to start survey session:', err);
          setError(`Failed to start survey: ${err.message}`);
        } finally {
          setLoading(false);
        }
      }
    });

    return () => unsubscribe();
    // We only want this effect to run once, based on the accessToken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  if (error) {
    return (
      <div className="min-h-screen bg-nova-bg flex items-center justify-center p-4">
        <div className="text-red-400 text-lg text-center">{error}</div>
      </div>
    );
  }

  // Always render the chat interface, even during loading
  // Pass null values during loading to trigger skeleton UI
  return (
    <SurveyChatInterface
      user={user}
      sessionId={sessionData?.sessionId || null}
      surveyTitle={sessionData?.survey_title || null}
      surveyDescription={sessionData?.survey_description || null}
      firstQuestion={sessionData?.first_question || null}
      isLoading={loading}
      selectedLanguage={selectedLanguage}
      onLanguageChange={startSessionWithLanguage}
    />
  );
};

export default SurveyPage;